import "@/App.css";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { 
  Wrench, 
  FileText, 
  DollarSign, 
  Users, 
  Megaphone, 
  ClipboardCheck, 
  Calculator, 
  Gavel, 
  HeadphonesIcon,
  Mail,
  Phone,
  Menu,
  X,
  ChevronDown
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cayman-rentals/artifacts/o0ul97bh_Screenshot%202026-02-18%20164721.png";

// Navigation Component
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#home", label: "Home" },
    { href: "#about", label: "About" },
    { href: "#services", label: "Services" },
    { href: "#contact", label: "Contact" },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 nav-glass ${isScrolled ? 'scrolled' : ''}`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a 
            href="#home" 
            onClick={(e) => scrollToSection(e, "#home")}
            className="flex items-center"
            data-testid="logo-link"
          >
            <img 
              src={LOGO_URL} 
              alt="JT Whitehouse Property Management" 
              className="h-14 w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-12">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className="nav-link font-body text-sm font-medium text-brand-teal uppercase tracking-widest px-2"
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:block">
            <a
              href="#contact"
              onClick={(e) => scrollToSection(e, "#contact")}
              className="btn-primary px-6 py-3 text-sm font-medium uppercase tracking-widest"
              data-testid="nav-cta"
            >
              Get in Touch
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-brand-teal"
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mobile-menu absolute top-20 left-0 right-0 py-6 px-6 border-t border-brand-gold/20">
            <div className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="font-body text-base font-medium text-brand-teal py-2"
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={(e) => scrollToSection(e, "#contact")}
                className="btn-primary px-6 py-3 text-center text-sm font-medium uppercase tracking-widest mt-4"
              >
                Get in Touch
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      id="home" 
      className="relative min-h-screen flex items-center justify-center"
      data-testid="hero-section"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920')"
        }}
      />
      <div className="absolute inset-0 hero-overlay" />
      
      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
        <p className="animate-section stagger-1 text-brand-gold text-sm font-body font-medium uppercase tracking-[0.3em] mb-6">
          Cayman Islands Property Management
        </p>
        <h1 className="animate-section stagger-2 font-heading text-4xl sm:text-5xl md:text-7xl font-semibold text-white leading-tight mb-8">
          Exceptional Property Management in Paradise
        </h1>
        <p className="animate-section stagger-3 font-body text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-12 leading-relaxed">
          Professional, reliable property management services tailored for the unique needs of Cayman Islands property owners.
        </p>
        <div className="animate-section stagger-4 flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="#services"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector("#services").scrollIntoView({ behavior: "smooth" });
            }}
            className="btn-primary px-10 py-4 text-sm font-medium uppercase tracking-widest"
            data-testid="hero-services-btn"
          >
            View Our Services
          </a>
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector("#contact").scrollIntoView({ behavior: "smooth" });
            }}
            className="btn-outline border-white text-white hover:bg-white hover:text-brand-teal px-10 py-4 text-sm font-medium uppercase tracking-widest"
            data-testid="hero-contact-btn"
          >
            Contact Us
          </a>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <ChevronDown className="text-white/60" size={32} />
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section 
      id="about" 
      className="py-24 md:py-32 bg-brand-sand"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
          {/* Text Content */}
          <div className="order-2 md:order-1">
            <p className="text-brand-gold text-sm font-body font-medium uppercase tracking-[0.3em] mb-4">
              About Us
            </p>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-brand-teal leading-tight mb-6">
              Your Trusted Property Partner in the Cayman Islands
            </h2>
            <div className="space-y-4 text-brand-teal/70 font-body text-lg leading-relaxed">
              <p>
                JT Whitehouse Property Management brings years of local expertise and a commitment to excellence in managing residential and commercial properties throughout the Cayman Islands.
              </p>
              <p>
                We understand the unique challenges and opportunities of Caribbean property ownership. Our dedicated team ensures your investment is protected, well-maintained, and profitable.
              </p>
              <p>
                From tenant relations to property maintenance, we handle every aspect of property management with professionalism and care, giving you peace of mind whether you're on-island or abroad.
              </p>
            </div>
            <div className="mt-8 pt-8 border-t border-brand-gold/20">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-brand-gold/10 flex items-center justify-center">
                  <Phone className="text-brand-gold" size={20} />
                </div>
                <div>
                  <p className="text-sm text-brand-teal/60 font-body">Ready to discuss your property?</p>
                  <a 
                    href="mailto:info@jtpropertymanagement.ws"
                    className="text-brand-gold font-medium hover:text-brand-gold/80 transition-colors duration-300"
                  >
                    info@jtpropertymanagement.ws
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Image */}
          <div className="order-1 md:order-2">
            <div className="image-offset">
              <img 
                src="https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Luxury tropical property"
                className="w-full aspect-[4/3] object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: Wrench,
      title: "Property Maintenance",
      description: "Comprehensive maintenance services to keep your property in pristine condition, from routine upkeep to emergency repairs."
    },
    {
      icon: FileText,
      title: "Lease Administration",
      description: "Expert handling of all lease documentation, renewals, and compliance to protect your interests."
    },
    {
      icon: DollarSign,
      title: "Rent Collection",
      description: "Reliable and efficient rent collection services with detailed financial reporting."
    },
    {
      icon: Users,
      title: "Tenant Screening",
      description: "Thorough background checks and screening to find quality, reliable tenants for your property."
    },
    {
      icon: Megaphone,
      title: "Vacancy Advertising",
      description: "Strategic marketing and advertising to minimize vacancy periods and attract ideal tenants."
    },
    {
      icon: ClipboardCheck,
      title: "Routine Inspections",
      description: "Regular property inspections to ensure tenant compliance and identify maintenance needs early."
    },
    {
      icon: Calculator,
      title: "Financial Management",
      description: "Accurate record-keeping and financial management of all property-related expenses and income."
    },
    {
      icon: Gavel,
      title: "Eviction Services",
      description: "Professional handling of eviction proceedings when necessary, following all legal requirements."
    },
    {
      icon: HeadphonesIcon,
      title: "Tenant Relations",
      description: "Responsive management of tenant concerns and issues to maintain positive landlord-tenant relationships."
    }
  ];

  return (
    <section 
      id="services" 
      className="py-24 md:py-32 bg-white palm-texture"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <div className="text-center mb-16 md:mb-20">
          <p className="text-brand-gold text-sm font-body font-medium uppercase tracking-[0.3em] mb-4">
            What We Offer
          </p>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold text-brand-teal leading-tight mb-6">
            Our Services
          </h2>
          <p className="font-body text-lg text-brand-teal/70 max-w-2xl mx-auto">
            Comprehensive property management solutions designed to protect your investment and maximize returns.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {services.map((service, index) => (
            <div 
              key={service.title}
              className={`service-card p-8 animate-section stagger-${index + 1}`}
              data-testid={`service-card-${index}`}
            >
              <div className="w-14 h-14 rounded-full bg-brand-gold/10 flex items-center justify-center mb-6 group-hover:bg-brand-gold/20 transition-colors duration-300">
                <service.icon className="text-brand-gold" size={24} strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-xl font-semibold text-brand-teal mb-3">
                {service.title}
              </h3>
              <p className="font-body text-brand-teal/70 leading-relaxed">
                {service.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <section 
      id="contact" 
      className="py-24 md:py-32 bg-brand-sand relative"
      data-testid="contact-section"
    >
      {/* Subtle texture */}
      <div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1732287322746-48f378f418e0?crop=entropy&cs=srgb&fm=jpg&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center"
        }}
      />
      
      <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
        <p className="text-brand-gold text-sm font-body font-medium uppercase tracking-[0.3em] mb-4">
          Get in Touch
        </p>
        <h2 className="font-heading text-3xl md:text-5xl font-semibold text-brand-teal leading-tight mb-6">
          Peace of Mind for Your Property
        </h2>
        <p className="font-body text-lg text-brand-teal/70 max-w-2xl mx-auto mb-12">
          Ready to experience worry-free property ownership? Contact us today to discuss how we can help manage your Cayman Islands property.
        </p>

        {/* Contact Info */}
        <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16">
          <a 
            href="mailto:info@jtpropertymanagement.ws"
            className="flex items-center gap-4 group"
            data-testid="contact-email"
          >
            <div className="w-16 h-16 rounded-full bg-brand-gold flex items-center justify-center group-hover:bg-brand-gold/90 transition-colors duration-300">
              <Mail className="text-white" size={28} strokeWidth={1.5} />
            </div>
            <div className="text-left">
              <p className="text-sm text-brand-teal/60 font-body mb-1">Email Us</p>
              <p className="text-lg font-medium text-brand-teal group-hover:text-brand-gold transition-colors duration-300">
                info@jtpropertymanagement.ws
              </p>
            </div>
          </a>

          <a 
            href="tel:+13459263683"
            className="flex items-center gap-4 group"
            data-testid="contact-phone"
          >
            <div className="w-16 h-16 rounded-full bg-brand-gold flex items-center justify-center group-hover:bg-brand-gold/90 transition-colors duration-300">
              <Phone className="text-white" size={28} strokeWidth={1.5} />
            </div>
            <div className="text-left">
              <p className="text-sm text-brand-teal/60 font-body mb-1">Call Us</p>
              <p className="text-lg font-medium text-brand-teal group-hover:text-brand-gold transition-colors duration-300">
                1-345-926-3683
              </p>
            </div>
          </a>
        </div>

        {/* CTA */}
        <div className="mt-12">
          <a
            href="mailto:info@jtpropertymanagement.ws"
            className="btn-primary inline-block px-12 py-5 text-sm font-medium uppercase tracking-widest"
            data-testid="contact-cta"
          >
            Send Us an Email
          </a>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer 
      className="bg-brand-teal py-12 md:py-16"
      data-testid="footer"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          {/* Logo */}
          <div className="flex items-center bg-white/10 p-2 rounded">
            <img 
              src={LOGO_URL} 
              alt="JT Whitehouse Property Management" 
              className="h-12 w-auto"
            />
          </div>

          {/* Links */}
          <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8">
            <a 
              href="mailto:info@jtpropertymanagement.ws"
              className="text-white/70 hover:text-brand-gold transition-colors duration-300 font-body text-sm"
            >
              info@jtpropertymanagement.ws
            </a>
            <Link 
              to="/privacy"
              className="text-white/70 hover:text-brand-gold transition-colors duration-300 font-body text-sm"
              data-testid="footer-privacy-link"
            >
              Privacy Policy
            </Link>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-8 pt-8 border-t border-white/10 text-center">
          <p className="text-white/50 font-body text-sm">
            © {new Date().getFullYear()} JT Whitehouse Property Management. All rights reserved.
          </p>
          <p className="text-white/30 font-body text-xs mt-2">
            www.jtpropertymanagement.ws
          </p>
          <p className="text-white/40 font-body text-xs mt-3">
            Website developed by{' '}
            <a 
              href="mailto:info@mbscayman.com?subject=Website%20Development%20inquiry"
              className="text-brand-gold/70 hover:text-brand-gold transition-colors duration-300"
            >
              Modern Business Solutions
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

// Home Page Component
const HomePage = () => {
  return (
    <>
      <Navbar />
      <main>
        <HeroSection />
        <AboutSection />
        <ServicesSection />
        <ContactSection />
      </main>
      <Footer />
    </>
  );
};

// Privacy Policy Page
const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-brand-sand">
      {/* Header */}
      <div className="bg-brand-teal py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center bg-white/10 p-2 rounded" data-testid="privacy-logo-link">
              <img 
                src={LOGO_URL} 
                alt="JT Whitehouse Property Management" 
                className="h-12 w-auto"
              />
            </Link>
            <Link 
              to="/"
              className="text-white/70 hover:text-brand-gold transition-colors duration-300 font-body text-sm uppercase tracking-widest"
              data-testid="privacy-back-link"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="privacy-content">
        <h1 className="font-heading text-3xl md:text-5xl font-semibold text-brand-teal mb-8">
          Privacy Policy
        </h1>
        <p className="text-brand-teal/60 font-body mb-8">
          Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </p>

        <div className="space-y-8 font-body text-brand-teal/80 leading-relaxed">
          <section>
            <h2 className="font-heading text-2xl font-semibold text-brand-teal mb-4">1. Introduction</h2>
            <p>
              JT Whitehouse Property Management ("we," "our," or "us") respects your privacy and is committed to protecting your personal data. This privacy policy explains how we collect, use, disclose, and safeguard your information when you visit our website www.jtpropertymanagement.ws or use our property management services.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-brand-teal mb-4">2. Information We Collect</h2>
            <p className="mb-4">We may collect information about you in various ways, including:</p>
            <ul className="list-disc list-inside space-y-2 pl-4">
              <li><strong>Personal Data:</strong> Name, email address, phone number, mailing address, and other contact information you voluntarily provide.</li>
              <li><strong>Property Information:</strong> Details about properties you own or manage, including addresses, rental history, and financial records related to property management services.</li>
              <li><strong>Usage Data:</strong> Information about how you access and use our website, including IP address, browser type, pages visited, and time spent on pages.</li>
              <li><strong>Communication Data:</strong> Records of correspondence when you contact us via email or other means.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-brand-teal mb-4">3. How We Use Your Information</h2>
            <p className="mb-4">We use the information we collect to:</p>
            <ul className="list-disc list-inside space-y-2 pl-4">
              <li>Provide, operate, and maintain our property management services</li>
              <li>Communicate with you about your property, tenants, and related matters</li>
              <li>Process payments and maintain accurate financial records</li>
              <li>Respond to your inquiries and provide customer support</li>
              <li>Send administrative information, updates, and marketing communications</li>
              <li>Comply with legal obligations and protect our legal rights</li>
              <li>Improve our website and services</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-brand-teal mb-4">4. Information Sharing and Disclosure</h2>
            <p className="mb-4">We may share your information with:</p>
            <ul className="list-disc list-inside space-y-2 pl-4">
              <li><strong>Service Providers:</strong> Third-party vendors who assist us in providing our services, such as maintenance contractors, payment processors, and IT support.</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or governmental authority.</li>
              <li><strong>Business Transfers:</strong> In connection with any merger, sale of company assets, or acquisition.</li>
              <li><strong>With Your Consent:</strong> When you have given us permission to share your information.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-brand-teal mb-4">5. Data Security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-brand-teal mb-4">6. Data Retention</h2>
            <p>
              We retain your personal information for as long as necessary to fulfill the purposes outlined in this privacy policy, unless a longer retention period is required or permitted by law. Property management records may be retained for extended periods as required by Cayman Islands regulations.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-brand-teal mb-4">7. Your Rights</h2>
            <p className="mb-4">Depending on your location, you may have the following rights regarding your personal data:</p>
            <ul className="list-disc list-inside space-y-2 pl-4">
              <li>Access to your personal information</li>
              <li>Correction of inaccurate or incomplete data</li>
              <li>Deletion of your personal data</li>
              <li>Restriction of processing</li>
              <li>Data portability</li>
              <li>Objection to processing</li>
            </ul>
            <p className="mt-4">To exercise any of these rights, please contact us at info@jtpropertymanagement.ws.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-brand-teal mb-4">8. Cookies and Tracking</h2>
            <p>
              Our website may use cookies and similar tracking technologies to enhance your browsing experience. You can set your browser to refuse all or some browser cookies, but this may limit your ability to use certain features of our website.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-brand-teal mb-4">9. Third-Party Links</h2>
            <p>
              Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-brand-teal mb-4">10. Children's Privacy</h2>
            <p>
              Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected personal data from a child without parental consent, we will take steps to delete that information.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-brand-teal mb-4">11. Changes to This Policy</h2>
            <p>
              We may update this privacy policy from time to time. We will notify you of any changes by posting the new privacy policy on this page and updating the "Last updated" date. We encourage you to review this policy periodically.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-brand-teal mb-4">12. Contact Us</h2>
            <p className="mb-4">
              If you have questions or concerns about this privacy policy or our data practices, please contact us at:
            </p>
            <div className="bg-white p-6 border border-brand-gold/20">
              <p className="font-semibold text-brand-teal">JT Whitehouse Property Management</p>
              <p>Email: <a href="mailto:info@jtpropertymanagement.ws" className="text-brand-gold hover:underline">info@jtpropertymanagement.ws</a></p>
              <p>Website: www.jtpropertymanagement.ws</p>
              <p>Cayman Islands</p>
            </div>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-brand-teal py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-white/50 font-body text-sm">
            © {new Date().getFullYear()} JT Whitehouse Property Management. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
